<script lang="ts">
  import * as d3 from 'd3';

  interface DataPoint {
    date: string;
    value: number;
    series?: string;
  }

  interface Props {
    data: DataPoint[];
    width?: number;
    height?: number;
    margin?: { top: number; right: number; bottom: number; left: number };
    showGrid?: boolean;
    showArea?: boolean;
    multiLine?: boolean;
    title?: string;
    yAxisLabel?: string;
    /** Tick labels on the Y axis (defaults to formatValue). Use plain numbers when the axis title carries units. */
    formatYTick?: (value: number) => string;
    formatValue?: (value: number) => string;
    formatXAxis?: (date: Date) => string;
    seriesColors?: Record<string, string>;
    yMin?: number;
    yMax?: number;
    /** Visual theme for grid/axis colors */
    variant?: 'light' | 'dark';
  }

  const {
    data,
    width = 600,
    height = 300,
    margin = { top: 20, right: 30, bottom: 40, left: 56 },
    showGrid = true,
    showArea = true,
    multiLine = false,
    title,
    yAxisLabel = '',
    formatYTick,
    formatValue = (v: number) => `$${v.toFixed(2)}`,
    formatXAxis,
    seriesColors = {},
    yMin,
    yMax,
    variant = 'light',
  }: Props = $props();

  const tickFormat = $derived(formatYTick ?? formatValue);

  const colors = $derived(
    variant === 'dark'
      ? {
          grid: '#334155',
          axis: '#64748b',
          tick: '#94a3b8',
          title: '#cbd5e1',
        }
      : {
          grid: '#e5e7eb',
          axis: '#9ca3af',
          tick: '#6b7280',
          title: '#64748b',
        }
  );

  // Extra left room when tick labels are wide; axis title lives outside the SVG.
  const chartMargin = $derived({
    top: margin.top,
    right: margin.right,
    bottom: margin.bottom,
    left: margin.left,
  });

  // Chart dimensions
  const innerWidth = $derived(width - chartMargin.left - chartMargin.right);
  const innerHeight = $derived(height - chartMargin.top - chartMargin.bottom);

  // Parse dates
  const parsedData = $derived(
    data.map(d => ({
      ...d,
      parsedDate: new Date(d.date)
    }))
  );

  // Group by series if multiLine
  const seriesData = $derived(() => {
    if (!multiLine) {
      return [{ key: 'default', values: parsedData }];
    }
    const grouped = d3.group(parsedData, d => d.series || 'default');
    return Array.from(grouped, ([key, values]) => ({ key, values }));
  });

  // Default colors for multiple lines
  const lineColors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16'
  ];

  // Scales
  const xScale = $derived(
    d3.scaleTime()
      .domain(d3.extent(parsedData, d => d.parsedDate) as [Date, Date])
      .range([0, innerWidth])
  );

  const yMaxValue = $derived(() => {
    if (yMax !== undefined) return yMax;
    return (d3.max(parsedData, d => d.value) || 0) * 1.1;
  });

  const yMinValue = $derived(() => {
    if (yMin !== undefined) return yMin;
    return 0;
  });
  
  const yScale = $derived(
    d3.scaleLinear()
      .domain([yMinValue(), yMaxValue()])
      .nice()
      .range([innerHeight, 0])
  );

  // Line generator
  const lineGenerator = $derived(
    d3.line<typeof parsedData[0]>()
      .x(d => xScale(d.parsedDate))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX)
  );

  // Area generator
  const areaGenerator = $derived(
    d3.area<typeof parsedData[0]>()
      .x(d => xScale(d.parsedDate))
      .y0(innerHeight)
      .y1(d => yScale(d.value))
      .curve(d3.curveMonotoneX)
  );

  // Axis ticks
  const xTicks = $derived(xScale.ticks(6));
  const yTicks = $derived(yScale.ticks(5));

  // Hover state
  let hoveredPoint = $state<{ x: number; y: number; value: number; date: Date } | null>(null);
  let mouseX = $state(0);

  function handleMouseMove(event: MouseEvent) {
    const svgRect = (event.currentTarget as SVGElement).getBoundingClientRect();
    const x = event.clientX - svgRect.left - chartMargin.left;
    
    if (x >= 0 && x <= innerWidth) {
      mouseX = x;
      const date = xScale.invert(x);
      
      // Find closest data point
      const bisect = d3.bisector<typeof parsedData[0], Date>(d => d.parsedDate).left;
      const index = bisect(parsedData, date);
      
      if (index > 0 && index < parsedData.length) {
        const d0 = parsedData[index - 1];
        const d1 = parsedData[index];
        const closest = date.getTime() - d0.parsedDate.getTime() > d1.parsedDate.getTime() - date.getTime() ? d1 : d0;
        
        hoveredPoint = {
          x: xScale(closest.parsedDate),
          y: yScale(closest.value),
          value: closest.value,
          date: closest.parsedDate
        };
      }
    }
  }

  function handleMouseLeave() {
    hoveredPoint = null;
  }

  function formatDate(date: Date): string {
    if (formatXAxis) {
      return formatXAxis(date);
    }
    return d3.timeFormat('%b %d')(date);
  }

  // Get color for a series (custom or from default palette)
  function getSeriesColor(seriesKey: string, index: number): string {
    if (seriesColors && seriesColors[seriesKey]) {
      return seriesColors[seriesKey];
    }
    return lineColors[index % lineColors.length];
  }
</script>

<div class="line-chart-container" class:has-y-label={Boolean(yAxisLabel)}>
  {#if title}
    <h3 class="text-sm font-medium text-gray-700 mb-2">{title}</h3>
  {/if}

  <div class="line-chart-layout">
    {#if yAxisLabel}
      <div class="y-axis-title-wrap" aria-hidden="true">
        <span class="y-axis-title" class:dark={variant === 'dark'}>{yAxisLabel}</span>
      </div>
    {/if}

    <svg
      {width}
      {height}
      class="line-chart"
      role="img"
      aria-label={title || yAxisLabel || 'Line chart'}
      onmousemove={handleMouseMove}
      onmouseleave={handleMouseLeave}
    >
    <g transform="translate({chartMargin.left}, {chartMargin.top})">
      <!-- Grid lines -->
      {#if showGrid}
        <g class="grid">
          {#each yTicks as tick}
            <line
              x1="0"
              x2={innerWidth}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke={colors.grid}
              stroke-dasharray="3,3"
            />
          {/each}
        </g>
      {/if}

      <!-- Area fill -->
      {#if showArea && !multiLine}
        <path
          d={areaGenerator(parsedData)}
          fill="url(#areaGradient)"
          opacity="0.3"
        />
      {/if}

      <!-- Lines -->
      {#each seriesData() as series, i}
        <path
          d={lineGenerator(series.values)}
          fill="none"
          stroke={getSeriesColor(series.key, i)}
          stroke-width="2"
          class="transition-all duration-200"
        />
      {/each}

      <!-- Data points (hidden for multiLine to reduce clutter) -->
      {#if !multiLine}
        {#each parsedData as point}
          <circle
            cx={xScale(point.parsedDate)}
            cy={yScale(point.value)}
            r="3"
            fill="#3b82f6"
            class="transition-all duration-200"
            opacity={hoveredPoint && Math.abs(xScale(point.parsedDate) - hoveredPoint.x) < 10 ? 1 : 0.5}
          />
        {/each}
      {/if}

      <!-- X Axis -->
      <g transform="translate(0, {innerHeight})">
        <line x1="0" x2={innerWidth} y1="0" y2="0" stroke={colors.axis} />
        {#each xTicks as tick}
          <g transform="translate({xScale(tick)}, 0)">
            <line y1="0" y2="6" stroke={colors.axis} />
            <text
              y="20"
              text-anchor="middle"
              class="text-xs tabular-nums"
              fill={colors.tick}
            >
              {formatDate(tick)}
            </text>
          </g>
        {/each}
      </g>

      <!-- Y Axis -->
      <g>
        <line x1="0" x2="0" y1="0" y2={innerHeight} stroke={colors.axis} />
        {#each yTicks as tick}
          <g transform="translate(0, {yScale(tick)})">
            <line x1="-6" x2="0" stroke={colors.axis} />
            <text
              x="-10"
              dy="0.35em"
              text-anchor="end"
              class="text-xs tabular-nums"
              fill={colors.tick}
            >
              {tickFormat(tick)}
            </text>
          </g>
        {/each}
      </g>

      <!-- Hover indicator -->
      {#if hoveredPoint}
        <g>
          <line
            x1={hoveredPoint.x}
            x2={hoveredPoint.x}
            y1="0"
            y2={innerHeight}
            stroke="#3b82f6"
            stroke-dasharray="3,3"
            opacity="0.5"
          />
          <circle
            cx={hoveredPoint.x}
            cy={hoveredPoint.y}
            r="6"
            fill="#3b82f6"
            stroke="white"
            stroke-width="2"
          />
        </g>
      {/if}

      <!-- Gradient definition -->
      <defs>
        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#3b82f6" stop-opacity="0.4" />
          <stop offset="100%" stop-color="#3b82f6" stop-opacity="0" />
        </linearGradient>
      </defs>
    </g>
  </svg>
  </div>

  <!-- Tooltip -->
  {#if hoveredPoint}
    <div
      class="tooltip absolute bg-gray-900 text-white px-3 py-2 rounded-lg text-sm shadow-lg pointer-events-none z-50"
      style="left: {chartMargin.left + hoveredPoint.x + (yAxisLabel ? 40 : 0)}px; top: {chartMargin.top + hoveredPoint.y - 50}px; transform: translateX(-50%);"
    >
      <div class="font-medium">{formatDate(hoveredPoint.date)}</div>
      <div class="text-blue-300">{formatValue(hoveredPoint.value)}</div>
    </div>
  {/if}
</div>

<style>
  .line-chart-container {
    position: relative;
  }

  .line-chart-layout {
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
    min-width: 0;
  }

  .y-axis-title-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 1.25rem;
    padding-bottom: 2.5rem;
  }

  .y-axis-title {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgb(100 116 139);
    white-space: nowrap;
  }

  .y-axis-title.dark {
    color: rgb(203 213 225);
  }

  .line-chart {
    overflow: visible;
    flex: 1 1 auto;
    min-width: 0;
  }
</style>

